/**
 * Composant affichant les stats globales de la plateforme.
 * Affiche UNIQUEMENT des agrégats (pas de détails).
 * Conforme WCAG 2.1 AA.
 */
import React from 'react';
import { Users, Bot, BookOpen, MessageSquare, HardDrive, UsersRound } from 'lucide-react';

interface PlatformStatsProps {
  data: {
    total_users: number;
    total_agents: number;
    total_subjects: number;
    total_media: number;
    total_conversations: number;
    total_teams: number;
    total_storage_bytes: number;
  };
}

export default function PlatformStats({ data }: PlatformStatsProps) {
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const stats = [
    {
      label: 'Utilisateurs',
      value: data.total_users,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Agents',
      value: data.total_agents,
      icon: Bot,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'Connaissances',
      value: data.total_subjects,
      icon: BookOpen,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'Médias',
      value: data.total_media,
      icon: HardDrive,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      label: 'Conversations',
      value: data.total_conversations,
      icon: MessageSquare,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
    },
    {
      label: 'Équipes',
      value: data.total_teams,
      icon: UsersRound,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div
            key={stat.label}
            className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
            role="article"
            aria-label={`${stat.label}: ${stat.value}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-zinc-900 dark:text-white">{stat.value.toLocaleString()}</p>
              </div>
              <div className={`${stat.bgColor} dark:bg-zinc-800 p-3 rounded-lg`}>
                <Icon className={`w-6 h-6 ${stat.color} dark:text-zinc-400`} aria-hidden="true" />
              </div>
            </div>
          </div>
        );
      })}

      {/* Storage card */}
      <div 
        className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900" 
        role="article" 
        aria-label={`Stockage total: ${formatBytes(data.total_storage_bytes)}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">Stockage Total</p>
            <p className="text-3xl font-bold text-zinc-900 dark:text-white">{formatBytes(data.total_storage_bytes)}</p>
          </div>
          <div className="bg-yellow-50 dark:bg-zinc-800 p-3 rounded-lg">
            <HardDrive className="w-6 h-6 text-yellow-600 dark:text-zinc-400" aria-hidden="true" />
          </div>
        </div>
      </div>
    </div>
  );
}
